import omit from 'lodash/omit';
import clone from 'lodash/clone';
import groupBy from 'lodash/groupBy';
import { notifyWarn } from '@shakkerai/pro-components';
import { fetchGenerationInfo } from '@shakkerai/pro-hooks';
import { createUniqid, pickResultFromElement } from '../utils';
import { getProcessors } from '../extension/flow';
import { presetProcessors } from '../presets/flow';
import { setProjectState, getProjectState } from '../store';
import { scrollIntoView, tryExecute } from '../utils/base';
import { flowScrollAreaId, registerType } from '../constants';
import { eventBus } from './eventBus';
import { FlOW_PROJECT_INFO, FlOW_SOCKET_INFO, FlOW_TYPE } from '../components/flow/constants';
import { findQueueList } from '../components/flow/components/FlowContainer/FlowEntry/utils';
import { getProjectId } from '../utils';

export function getDefaultUnitId() {
  const units = getProjectState('units');
  return units?.nodes?.find((id) => id.startsWith('node-generator'));
}

export const getProcessor = (type) => {
  const extensionProcessors = getProcessors();
  return extensionProcessors[type] || presetProcessors[type];
};

export const saveFlowWhenUpdate = (flowId) => {
  eventBus.emit(FlOW_SOCKET_INFO, {
    flowId,
    tp: FlOW_TYPE.UPDATE,
  });
};

export const updateFlow = (flowId, partialData) => {
  setProjectState({
    flows: getProjectState('flows').map((item) => {
      return item.id === flowId ? tryExecute(partialData, item) : item;
    }),
  });
  eventBus.emit(FlOW_SOCKET_INFO, {
    flowId,
    tp: FlOW_TYPE.UPDATE,
  });
};

export const removeFlow = (flowId) => {
  const flows = getProjectState('flows');
  const target = flows.find((flow) => flow.flowId === flowId);
  setProjectState({
    flows: flows.filter((flow) => flow.flowId !== flowId),
  });
  eventBus.emit(FlOW_SOCKET_INFO, { flowId, tp: FlOW_TYPE.DEL, target });
};

export const editFlowLbabel = (flowId, value) => {
  const flows = getProjectState('flows');
  setProjectState({
    flows: [...flows].map((flow) => {
      if (flow.flowId === flowId) {
        flow.data.title = value;
        flow.state = [];
      }
      return flow;
    }),
  });
  eventBus.emit(FlOW_SOCKET_INFO, { flowId, tp: FlOW_TYPE.UPDATE });
};

export const delUnit = (id) => {
  const { units, nodeMap } = getProjectState();

  const tempNodes = units.nodes.filter((nodeId) => nodeId !== id);
  if (!tempNodes.some((nodeId) => nodeId.includes('generator'))) {
    notifyWarn('最后一个主生成器受到保护，请删除其他工作单元');
    return false;
  }
  const newNodeMap = { ...nodeMap };
  delete newNodeMap[id];
  const nodes = units.nodes.filter((nodeId) => nodeId !== id);
  const newUnits = {
    ...units,
    nodes,
  };
  setProjectState({
    nodeMap: newNodeMap,
    units: newUnits,
  });
  if (nodes[0]) {
    autoSelectUnitOrNode(true, nodes[0]);
  }
  eventBus.emit(FlOW_PROJECT_INFO);
};

const getNodeData = (type, data, flowId) => {
  const config = getProcessor(type);
  const nodeData = tryExecute(config.defaultData, { ...data, flowId });
  return nodeData;
};

export const formatNode = (rest) => {
  const { type, title, label, data, flowId = '', config, result, inputs = [], outputs = [] } = rest;
  const id = `node-${createUniqid(type)}`;
  return {
    id,
    registerType: registerType.UNIT,
    data: {
      state: [],
      id,
      type,
      flowId,
      inputs,
      outputs,
      data: getNodeData(type, data, flowId),
      label: title || label || config?.title || '未命名节点',
      result: result || {},
    },
  };
};

const autoSelectUnitOrNode = (autoSelect = true, nodeId = '') => {
  autoSelect &&
    setProjectState({
      currentNodeId: nodeId,
    });
};

export const createUnit = (unit, { autoSelect } = {}) => {
  const {
    units: { nodes, ...rest },
    nodeMap,
  } = getProjectState();

  const newUnit = formatNode(unit);

  setProjectState({
    units: {
      ...rest,
      nodes: [...(nodes ?? []), newUnit.id],
    },
    nodeMap: { ...nodeMap, [newUnit.id]: newUnit },
  });

  autoSelectUnitOrNode(autoSelect, newUnit.id);
  eventBus.emit(FlOW_PROJECT_INFO);
  return newUnit.id;
};

// 流节点创建用这个，单元节点创建用上面的createUnit
export const createNode = (node, { autoSelect } = {}) => {
  const { nodeMap, flows } = getProjectState();
  const newNode = formatNode(node);

  const nextNodeMap = Object.assign({}, nodeMap, { [newNode.id]: newNode });

  setProjectState({
    nodeMap: nextNodeMap,
    flows: flows.map((flow) => {
      if (flow.id !== flowId) return flow;
      return Object.assign({}, flow, {
        nodes: flow.nodes.concat(newNode.id),
      });
    }),
  });

  autoSelectUnitOrNode(autoSelect, newNode.id);

  return newNode.id;
};

export const extendFlow = (flowId, { nodes, edges }) => {
  const { nodeMap, flows } = getProjectState();

  const nextNodeMap = Object.assign({}, nodeMap);

  // 新生成的节点
  const nodeIds = nodes.map((node) => {
    const id = node.id || `node-${createUniqid(node.type)}`;

    const newNode = {
      id,
      flowId,
      data: {
        flowId,
        id,
        label: node.label,
        type: node.type,
        data: node.data,
        inputs: node.inputs,
        outputs: node.outputs,
        result: node.result || {},
        state: [],
      },
    };

    nextNodeMap[newNode.id] = newNode;
    return newNode.id;
  });

  console.log(`[DEBUG] nodeIds`, nodeIds);

  let prevTailNodeId = null;

  const nextFlows = flows.map((flow) => {
    if (flow.flowId !== flowId) return flow;

    prevTailNodeId = flow.data.nodes.at(-1);

    return {
      ...flow,
      nodeOrderList: flow.nodeOrderList.concat(nodes.map((node) => node.id)),
      data: {
        ...flow.data,
        nodes: flow.data.nodes.concat(nodeIds),
        edge_lists: flow.data.edge_lists.concat(edges),
      },
    };
  });

  if (prevTailNodeId) {
    nextNodeMap[prevTailNodeId].outputs = (nextNodeMap[prevTailNodeId].outputs || []).concat(nodes[0].inputs || []);
  }

  setProjectState({
    nodeMap: nextNodeMap,
    flows: nextFlows,
  });

  eventBus.emit(FlOW_SOCKET_INFO, { flowId, tp: FlOW_TYPE.UPDATE });
  return [flowId, nodeIds.at(-1)];
};

export const checkShouldRebuildFlow = (flowId) => {
  const { flows, nodeMap } = getProjectState();
  const flow = flows.find((flow) => flow.flowId === flowId);

  if (!flow) return;

  const nodes = flow.data.nodes.map((nodeId) => nodeMap[nodeId]);

  const existNodes = {};
  const outputsMap = {};
  const removedNodes = {};
  const nextNodeMap = { ...nodeMap };

  let nextNodes = [];
  let nextEdges = [];
  let shouldRebuild = false;

  nodes.forEach((node) => {
    const processor = getProcessor(node.data.type);

    // 通过data计算真实的输入
    const realInputs = processor.getInputs(node.data.data);
    const realInputNames = Object.fromEntries(realInputs.map((input) => [input.name, input]));

    // 获取创建流的时候定���的输入
    const definedInputs = node.data.inputs;

    const nextNode = clone(node);

    nextNode.data.inputs = [];

    definedInputs.forEach((input) => {
      // 遍历所有之前定义的输入端口

      const realInput = realInputNames[input.name];

      if (!realInput) {
        // 如果定义的输入端口在实际的输入中不存在，则需要重新构建
        shouldRebuild = true;
        console.log(`[DEBUG] input port missing:`, node.data.label, ':', input.name);
      } else {
        existNodes[node.id] = node;
        existNodes[input.sourceUnitId] = node;

        nextEdges.push({
          id: createUniqid(),
          source: input.sourceUnitId,
          targetHandle: input.name,
          target: node.id,
          sourceHandle: node.id,
        });

        nextNode.data.inputs.push(input);
      }
    });

    nextNodes.push(nextNode);
  });

  nextNodes = nextNodes.filter((node) => {
    const isRemoved = !existNodes[node.id];

    if (isRemoved) {
      removedNodes[node.id] = node;
    }

    return !isRemoved;
  });

  nextEdges = nextEdges.map((item) => {
    // 一个source会存在连接到多个target的情况，因此需要为每个target指定一个index
    item.targetIndex = nextEdges.filter((edge) => edge.source === item.source).indexOf(item);

    outputsMap[item.source] = outputsMap[item.source] || [];
    outputsMap[item.source].push(item);

    return item;
  });

  nextNodes = nextNodes.map((node) => {
    node.outputs = outputsMap[node.id] || [];
    nextNodeMap[node.id] = node;
    return node;
  });

  return { flows, removedNodes, shouldRebuild, nextEdges, nextNodes, nextNodeMap };
};

export const resolveNodesInputs = (sourceNodes, tailNode) => {
  let edges = [];

  const sourceNodesMap = Object.fromEntries(
    sourceNodes.map((item) => [
      item.__id,
      omit(
        {
          ...item,
          id: `node-${createUniqid(item.type)}`,
        },
        '__id',
      ),
    ]),
  );

  const dedupedNodes = Object.values(sourceNodesMap).concat([
    {
      ...tailNode,
      id: `node-${createUniqid(tailNode.type)}`,
    },
  ]);

  const outputsMap = {};

  let nodes = dedupedNodes.map((node) => {
    if (node.inputs) {
      node.inputs = node.inputs.map((item) => {
        const sourceNodeId = sourceNodesMap[item.__id]?.id;

        if (sourceNodeId) {
          edges.push({
            id: createUniqid(),
            source: sourceNodeId,
            target: node.id,
            sourceHandle: node.id,
            targetHandle: item.name,
          });
        }

        return {
          name: item.name,
          type: item.type,
          value: item.value,
          label: item.label,
          sourceUnitId: sourceNodeId,
        };
      });
    } else {
      node.inputs = [];
    }

    return node;
  });

  edges = edges.map((item) => {
    // 一个source会存在连接到多个target的情况，因此需要为每个target指定一个index
    item.targetIndex = edges.filter((edge) => edge.source === item.source).indexOf(item);

    outputsMap[item.source] = outputsMap[item.source] || [];
    outputsMap[item.source].push(item);

    return item;
  });

  nodes = nodes.map((node) => {
    node.outputs = outputsMap[node.id] || [];
    return node;
  });

  return { nodes, edges };
};

// 通过图片的生成任务ID来构建流
export const findPreviousUnits = async (element) => {
  let nodes = [];

  let taskId = element.innerTaskId;
  let result = pickResultFromElement(element);

  if (taskId) {
    // 通过innerTaskId查找原始生图参数projectData
    let projectData = await fetchGenerationInfo(taskId);
    if (!projectData) return nodes;

    // 找出生图类型作为工作单元的类型
    let { generateType: unitType } = projectData;

    // 这里是一个兼容处理，旧版本的主生成器生图类型是normal
    if (unitType === 'normal') {
      unitType = 'generator';
    }

    // 获取工作单元的��理器配置
    const processor = getProcessor(unitType);

    if (processor.extractProjectData) {
      projectData = processor.extractProjectData(projectData);
    }

    nodes.unshift({
      __id: taskId,
      type: unitType,
      label: projectData.unitLabel || processor.title,
      data: Object.assign(projectData, {
        sourceElement: projectData.sourceElement,
      }),
      inputs: [],
      result: result ? { items: [result], ...result } : {},
    });

    if (processor && processor.getInputs) {
      // 调用处理器的getInputs方法来获取输入
      const inputs = processor.getInputs(projectData);

      const imageInputs = inputs.filter((item) => item.value && item.type === 'image');

      for (const item of imageInputs) {
        if (item.value.innerTaskId) {
          nodes[0].inputs.push({
            name: item.name,
            value: item.value,
            type: item.type,
            label: item.label,
            __id: item.value.innerTaskId,
          });

          nodes = nodes.concat(await findPreviousUnits(item.value));
        }
      }
    }
  }

  return nodes;
};

export const rebuildFlow = (flowId) => {
  const { flows, shouldRebuild, removedNodes, nextEdges, nextNodes, nextNodeMap } = checkShouldRebuildFlow(flowId);

  if (shouldRebuild) {
    setProjectState(
      {
        nodeMap: nextNodeMap,
        flows: flows.map((item) => {
          if (item.flowId !== flowId) return item;

          return {
            ...item,
            nodeOrderList: findQueueList(nextEdges),
            data: {
              ...item.data,
              nodes: nextNodes.map((node) => node.id),
              edge_lists: nextEdges,
            },
          };
        }),
      },
      {
        skipTracking: true,
      },
    );

    eventBus.emit(FlOW_SOCKET_INFO, { flowId, tp: FlOW_TYPE.UPDATE });
  }

  return { shouldRebuild, removedNodes };
};

export const createFlowFromElement = (elementId, type) => {};

export const createFlowFromUnit = async (unitId) => {
  // 检测当前节点的输入，判断是否需要创建流

  const { nodeMap, flows } = getProjectState();
  const unit = nodeMap[unitId];

  if (!unit) return { newFlowCreated: false };

  const { type, data } = unit.data;
  const processor = getProcessor(type);
  const inputs = processor.getInputs(data);
  const imageInputs = inputs.filter((input) => input.type === 'image' && input.value && input.value.innerTaskId);

  console.log(`[DEBUG] unit inputs`, inputs);

  if (imageInputs.length) {
    let nodes = [];
    const tailNode = { id: `node-${createUniqid(type)}`, label: processor.title, type, data, inputs: [], result: {} };

    for (const item of imageInputs) {
      if (item.value.innerTaskId) {
        tailNode.inputs.push({
          name: item.name,
          value: item.value,
          type: item.type,
          label: item.label,
          __id: item.value.innerTaskId,
        });

        try {
          nodes = nodes.concat(await findPreviousUnits(item.value));
        } catch (error) {
          console.log(`[DEBUG] error`, error);
        }
      }
    }

    if (!nodes.length) return { newFlowCreated: false };

    const [newFlowId, newUnitId] = createFlowWithNodes(resolveNodesInputs(nodes, tailNode));

    setProjectState({
      currentNodeId: newUnitId,
    });

    return { newFlowCreated: true, flowId: newFlowId, unitId: newUnitId };
  } else {
    return { newFlowCreated: false };
  }
};

globalThis.rebuildFlow = rebuildFlow;

// 创建流轨道
export const createFlowWithNodes = ({ nodes, edges }) => {
  console.log(`[DEBUG] nodes`, { nodes, edges });

  const flowId = createUniqid('flow');
  const { nodeMap, flows } = getProjectState();
  const nextNodeMap = Object.assign({}, nodeMap);

  const nodeIds = nodes.map((node) => {
    const id = node.id || `node-${createUniqid(node.type)}`;

    const newNode = {
      id,
      flowId,
      data: {
        flowId,
        id,
        label: node.label,
        type: node.type,
        data: node.data,
        inputs: node.inputs,
        outputs: node.outputs,
        result: node.result || {},
        state: [],
      },
    };

    nextNodeMap[newNode.id] = newNode;
    scrollIntoView(flowScrollAreaId);
    return newNode.id;
  });

  const flow = {
    nodeOrderList: findQueueList(edges),
    projectId: getProjectId(),
    flowId,
    registerType: registerType.FLOW,
    data: {
      title: '未命名流',
      edge_lists: edges,
      nodes: nodeIds,
    },
  };

  setProjectState(
    {
      nodeMap: nextNodeMap,
      flows: [flow].concat(flows),
    },
    {
      skipTracking: true,
    },
  );

  eventBus.emit(FlOW_SOCKET_INFO, { flowId, tp: FlOW_TYPE.NEW });
  return [flowId, nodeIds.at(-1)];
};

// 这个nodes为nodeMap里的node集合
export const findFistNodesIds = (edge_lists, nodes) => {
  const ids = [];
  nodes.forEach((node) => {
    const tagetId = node.id;
    if (edge_lists.some((edge) => edge.source === tagetId) && !edge_lists.some((edge) => edge.target === tagetId)) {
      ids.push(node.id);
    }
  });
  return ids;
};

export const updateUnit = (nodeId, partialData) => {
  const { nodeMap } = getProjectState();
  const nextNode = tryExecute(partialData, nodeMap[nodeId]);

  setProjectState({ nodeMap: Object.assign({}, nodeMap, { [nodeId]: nextNode }) });
  eventBus.emit(FlOW_PROJECT_INFO);
};

export const updateUnitResult = (nodeId, partialData) => {
  updateUnit(nodeId, (node) => {
    return {
      ...node,
      data: {
        ...(node?.data ?? {}),
        result: tryExecute(partialData, node?.data?.result, node),
      },
    };
  });
};

export const updateUnitData = (nodeId, partialData) => {
  nodeId = nodeId || getProjectState('currentNodeId');

  updateUnit(nodeId, (node) => {
    return {
      ...node,
      data: {
        ...(node?.data ?? {}),
        data: tryExecute(partialData, node?.data?.data, node),
      },
    };
  });
};

export const getUnitResult = (nodeId, fieldName) => {
  const { nodeMap, currentNodeId } = getProjectState();
  const unitResult = nodeMap[nodeId || currentNodeId]?.data?.result || {};
  return fieldName ? unitResult[fieldName] : unitResult;
};

export const getActiveGeneratorNodeId = () => {
  let { currentNodeId, nodeMap, units } = getProjectState();

  if (!nodeMap[currentNodeId] || nodeMap[currentNodeId]?.data?.type !== 'generator') {
    currentNodeId = null;
  }

  if (!currentNodeId) {
    // debugger;
    currentNodeId = units.nodes.find((nodeId) => nodeMap[nodeId]?.data?.type === 'generator');
  }

  return currentNodeId;
};

export const getUnitData = (nodeId, fieldName) => {
  const { nodeMap, currentNodeId } = getProjectState();
  const unitData = nodeMap[nodeId || currentNodeId]?.data?.data || {};
  return fieldName ? unitData[fieldName] : unitData;
};

export const getUnit = (nodeId) => {
  return getProjectState('nodeMap')[nodeId]?.data || {};
};

export const restoreUnitData = () => {};

export const removeUnit = (nodeId) => {
  const { nodeMap, flows, units } = getProjectState();
  const node = nodeMap[nodeId];
  const nextNodeMap = Object.assign({}, nodeMap, { [nodeId]: null });

  const updatedData = {
    nodeMap: nextNodeMap,
  };

  if (node) {
    if (node.flowId) {
      updatedData.flows = flows.map((flow) => {
        if (flow.id !== node.flowId) return flow;

        return Object.assign({}, flow, {
          nodes: flow.nodes.filter((id) => id !== nodeId),
        });
      });
    } else {
      updatedData.units = Object.assign({}, units, {
        nodes: units.nodes.filter((id) => id !== nodeId),
      });
    }
  }

  setProjectState(updatedData);
  eventBus.emit(FlOW_PROJECT_INFO);
};

export const sortUnits = (nodes) => {
  const data = getProjectState();
  setProjectState({
    ...data,
    units: {
      ...data.units,
      nodes,
    },
  });
  eventBus.emit(FlOW_PROJECT_INFO);
};

// 找到与url匹配的结果的索引
export const findMatchedResultIndex = (unitId) => {
  const { nodeMap } = getProjectState();
  const unit = nodeMap[unitId];
  const result = unit?.data?.result;
  const url = result?.url ?? '';

  if (!url) return -1;

  const items = result?.items ?? [];

  return items.findIndex((item) => item.url && item.url === url);
};

// 找到与url匹配的结果
export const findMatchedResultByUrl = (unitId) => {
  const { nodeMap } = getProjectState();
  const unit = nodeMap[unitId];
  const result = unit?.data?.result;
  const index = findMatchedResultIndex(unitId);

  return index !== -1 ? result?.items[index] : null;
};

// 根据画布元素最后一次操作的unit
export const findElementRelatedUnit = (elementId) => {
  const { nodeMap, elements } = getProjectState();
  const element = elements.find((element) => element.id === elementId);
  const unitId = element?.lastDerivativeUnitId;

  return unitId ? nodeMap[unitId] : null;
};

// 找到流在画布上显示的图片, 每条流只在画布上对应一个元素，类似一个坑位
export const findFlowRelatedElement = (flowId) => {
  const elements = getProjectState('elements');
  return elements.findLast((element) => element.flowId === flowId);
};

// 找到独立单元在画布上显示的图片, 每个单元在画布上对应一个元素
export const findUnitRelatedElement = (unitId) => {
  const elements = getProjectState('elements');
  return elements.find((item) => item.lastDerivativeUnitId === unitId);
};

// 获取流节点运行顺序
export function getFlowNodeOrderList(flowId) {
  const flow = getProjectState('flows').find((flow) => flow.flowId === flowId);
  return flow?.nodeOrderList ?? [];
}

export const findFlowFirstNodeId = (flowId) => {
  const nodeOrderList = getFlowNodeOrderList(flowId);
  return nodeOrderList[0] ?? '';
};

// 找到下一个要执行的nodeId
export const findFlowNextNodeId = (flowId, nodeId) => {
  const nodeOrderList = getFlowNodeOrderList(flowId);
  const currentNodeIndex = nodeOrderList.findIndex((id) => id === nodeId);

  return nodeOrderList[currentNodeIndex + 1] ?? '';
};

// 流模式开始自动运行, 退出编辑且清空选区
export function startAutoRunningFlow(flowId, nodeId) {
  setProjectState({
    autoRunningFlowId: flowId,
    autoRunningFlowNodeId: nodeId,
    editingElementId: '',
    selection: [],
  });
}

// 流模式结束自动运行
export function endAutoRunningFlow() {
  setProjectState({
    autoRunningFlowId: '',
    autoRunningFlowNodeId: '',
  });
}

// 检查是否执行到最后一个节点
export function checkReachLastNode(flowId) {
  const nodeOrderList = getFlowNodeOrderList(flowId);
  const autoRunningFlowNodeId = getProjectState('autoRunningFlowNodeId');

  return nodeOrderList[nodeOrderList.length - 1] === autoRunningFlowNodeId;
}

// 根据flowId获取flow下面节点的title
export function getFlowNodeTypes(flowId) {
  const nodes = getFlowNodeOrderList(flowId);
  const nodeMap = getProjectState('nodeMap');
  return nodes.map((nodeId) => nodeMap[nodeId]?.data?.type);
}

// 根据流id和当前节点id，获取上一个节点id
export function getPrevUnitId(flowId, unitId) {
  const nodeOrderList = getFlowNodeOrderList(flowId);
  const currentNodeIndex = nodeOrderList.findIndex((node) => node === unitId);

  return nodeOrderList[currentNodeIndex - 1];
}

/**
 * 重置流数据相关函数，生成新的flowId和节点id
 * 用于上传流文件及流自动运行时
 */
export const createNewNodeId = (originalId) => {
  const prefix = originalId.split('-').slice(0, 2);
  return [...prefix, createUniqid()].join('-');
};

const createNewNodeData = (node, newNodeId, newFlowId) => ({
  ...node,
  id: newNodeId,
  flowId: newFlowId,
  data: {
    ...(node?.data ?? {}),
    id: newNodeId,
    flowId: newFlowId,
    data: {
      ...(node?.data?.data ?? {}),
    },
  },
});

export function resetFlowData(
  flow,
  {
    replaceOldFlow = false, // 是否替换旧的flow
    clearImageUrl = false, // 是否清除图片url
  } = {},
) {
  if (!flow || !flow.data?.nodes) {
    return;
  }

  const nodeMap = getProjectState('nodeMap');
  const flows = getProjectState('flows');
  const oldFlowIndex = flows.findIndex((item) => item.flowId === flow.flowId);
  const newNodeMap = { ...nodeMap };
  const newFlow = { ...flow };
  const newFlowId = createUniqid('flow');

  // 更新flow ID
  newFlow.flowId = newFlowId;

  // 处理依赖关系里的节点ID
  let edgeString = JSON.stringify(newFlow.data.edge_lists || []);
  let nodeOrderList = JSON.stringify(newFlow.nodeOrderList || []);

  // 更新节点数据
  const nodeIdMap = new Map(); // 用于存储旧ID到新ID的映射
  newFlow.data.nodes = newFlow.data.nodes.map((node) => {
    console.log('[DEBUG] ~ newFlow.data.nodes=newFlow.data.nodes.map ~ node:', node);
    const newNodeId = createNewNodeId(node.id);
    nodeIdMap.set(node.id, newNodeId);

    if (clearImageUrl) {
      node.data.result = {};
    }

    // 更新节点映射
    newNodeMap[newNodeId] = createNewNodeData(node, newNodeId, newFlowId);

    return newNodeId;
  });

  // 更新边缘关系中的节点ID引用
  nodeIdMap.forEach((newId, oldId) => {
    edgeString = edgeString.replaceAll(oldId, newId);
    nodeOrderList = nodeOrderList.replaceAll(oldId, newId);
  });

  // 更新依赖关系
  newFlow.data.edge_lists = JSON.parse(edgeString);
  newFlow.nodeOrderList = JSON.parse(nodeOrderList);

  const newFlows =
    replaceOldFlow && oldFlowIndex !== -1
      ? [...flows.slice(0, oldFlowIndex), newFlow, ...flows.slice(oldFlowIndex + 1)]
      : [newFlow, ...flows];

  setProjectState({
    flows: newFlows,
    nodeMap: newNodeMap,
    isFoldFlow: false,
  });

  return newFlow;
}

// 重置流的部分节点, 用于在流单节点编辑时, 切换图片, 会更新当前节点之后所有节点的url和sourceElement
export function resetFlowAfterNode(flowId, nodeId) {
  const nodeOrderList = getFlowNodeOrderList(flowId);
  const currentNodeIndex = nodeOrderList.findIndex((node) => node === nodeId);
  const nextNodeIds = nodeOrderList.slice(currentNodeIndex + 1);

  nextNodeIds.forEach((nodeId) => {
    updateUnit(nodeId, (data) => ({
      ...data,
      data: {
        ...(data?.data ?? {}),
        data: {
          ...(data?.data?.data ?? {}),
          sourceElement: null, // 清空sourceElement
        },
        result: {},
        // result: {
        //   ...data.data.result,
        //   url: '', // 清空url
        // },
      },
    }));
  });
}

// 找到流节点所有待替换的图片, 用于统一替换
export function getReplaceInfo(flowId) {
  const flow = getProjectState('flows').find((flow) => flow.flowId === flowId);
  const nodeMap = getProjectState('nodeMap');
  const nodeOrderList = getFlowNodeOrderList(flowId);
  const result = [];

  // 找出头节点
  const beginIds = findFistNodesIds(
    flow?.data?.edge_lists ?? [],
    (flow?.data?.nodes ?? []).map((node) => nodeMap[node]),
  );
  console.log('[DEBUG] ~ getReplaceInfo ~ beginIds:', beginIds);

  nodeOrderList.forEach((nodeId, index) => {
    const nodeData = nodeMap[nodeId].data;
    const type = nodeData.type;
    const info = {
      id: nodeId,
      label: nodeData.label,
      imageList: [],
    };

    const processor = getProcessor(type);
    const inputs = processor.getInputs(nodeData.data);
    const hasSourceElement = inputs.find((input) => input.name === 'sourceElement');
    // 头节点处理
    if (beginIds.includes(nodeId)) {
      // 如果不是生成器且不包含sourceElement, 手动添加
      if (type !== 'generator' && type !== 'workflow' && !hasSourceElement) {
        info.imageList.push({
          name: 'sourceElement',
          type: 'image',
          label: '原图',
          value: null,
        });
      } else {
        // 否则直接过滤下非空的
        info.imageList.push(...inputs.filter((item) => !!item.value));
      }
    } else {
      // 非头结点如何处理
    }

    // 根据value.url进行聚合
    const grouped = groupBy(info.imageList, 'value.url');

    info.imageList = [...Object.values(grouped)];

    if (info.imageList.length > 0) {
      result.push(info);
    }
  });

  return result;
}

// 修改输入
export function applyInputToNode(nodeId, name, value) {
  const nodeMap = getProjectState('nodeMap');
  const nodeData = nodeMap[nodeId].data;
  const type = nodeData.type;
  const processor = getProcessor(type);
  const newData = processor.applyInput(name, value, nodeData.data);

  updateUnitData(nodeId, newData);
}

// 根据输出修改修改节点
export function applyOutputToNode(nodeId, value) {
  const nodeMap = getProjectState('nodeMap');
  const nodeData = nodeMap[nodeId].data;
  const type = nodeData.type;
  const processor = getProcessor(type);
  const outputs = nodeData.outputs;

  outputs.forEach((output) => {
    const newData = processor.applyInput(output.targetHandle, value, nodeData.data);
    updateUnitData(output.target, newData);
  });
}
